import React, { useState } from "react";
import styles from "./WalletTokenOutput.module.css";

// Display names for the wallet detected from the token output
const WALLET_LABELS = {
  applepay: "Apple Pay",
  googlepay: "Google Pay",
  samsungpay: "Samsung Pay",
};

const WalletTokenOutput = ({
  tokenOutput,
  prettyPrint,
  isAnimating,
  buttonRef,
  googlePaySettings,
  applePaySettings,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [requestPayload, setRequestPayload] = useState("");
  const [responsePayload, setResponsePayload] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const toggleModal = () => {
    setShowModal((prev) => !prev);
    setResponsePayload(null); // Reset the response payload state

    const parsedToken = JSON.parse(tokenOutput);
    let wallet;
    let tokenData;

    if (parsedToken.token) {
      wallet = "applepay";
      tokenData = JSON.stringify(parsedToken.token.paymentData, null, 2);
    } else if (parsedToken.tokenizationData) {
      wallet = "googlepay";
      tokenData = JSON.stringify(parsedToken.tokenizationData.token, null, 2);
    } else {
      wallet = "samsungpay";
      tokenData = JSON.stringify(parsedToken, null, 2);
    }

    setWalletType(wallet); // Update state
    handleRequestChange(wallet, tokenData);
  };

  function handleRequestChange(wallet, tokenData) {
    setRequestPayload(
      `{\n  "type": "${wallet}",\n  "token_data": ${tokenData}\n}`,
    );
  }

  const sendTokensRequest = async () => {
    // Mapping of Apple Pay merchant IDs to associated API keys
    const applePayAuthorizationKeys = {
      "merchant.wallets.test.suite": "pk_sbox_adduw2kq4kycw6pfpuduuper7ui",
      "merchant.sandbox.gateway": "pk_sbox_7fpb25qsjy7il6pcshi6ykdepiy",
      "merchant.sandbox.e2e": "pk_sbox_twxyu5bwhfumynl63yqjb7izei3",
      "merchant.china.mainland.rsa": "pk_sbox_yqezjw4odbkdlql2b3exs3qgqaa",
      "merchant.qa.stephen": "pk_qa_qewiru6dwbqb253kechiyjaj2uu",
      "merchant.prod.stephen": "pk_6oc6folla4mcsk4tmhurtwpzyia",
      // "merchant.prod.malpb": "pk_",
      "merchant.blue.emi.prod": "pk_wwevveiswrxkua4l4645tdspnih",
    };

    // Samsung Pay credentials aren't supported by the Checkout.com tokens API
    if (walletType === "samsungpay") {
      setResponsePayload("Tokenization not supported for Samsung Pay");
      return;
    }

    try {
      setIsLoading(true);

      const apiPublicKey =
        walletType === "applepay"
          ? applePayAuthorizationKeys[applePaySettings.merchantId] ||
            console.error(
              "KNET or QPay request - not compatible with CKO tokens",
            )
          : googlePaySettings.gatewayMerchantId;

      let url = "https://api.checkout.com/tokens"; // Default: Production

      // Check API key prefix first
      if (apiPublicKey.includes("_qa_")) {
        url = "https://gwc.ckotech.co/vault-proxy/tokens"; // QA
      } else if (apiPublicKey.includes("_sbox_")) {
        url = "https://api.sandbox.checkout.com/tokens"; // Sandbox
        // Check Apple Pay MID
      } else if (walletType === "applepay") {
        switch (applePaySettings.merchantId) {
          case "merchant.prod.stephen":
          // case "merchant.prod.malpb":
            url = "https://api.checkout.com/tokens"; // Production
            break;
          case "merchant.blue.emi.prod":
            url = "https://api.prod.blueemi.com/tokens"; // Blue EMI Production
            break;
          default:
            setResponsePayload("Tokenization not supported for KNET or QPay");
            setIsLoading(false);
            return;
        }
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: apiPublicKey,
        },
        body: requestPayload,
      });

      const data = await response.json();
      setResponsePayload(JSON.stringify(data, null, 2));
    } catch (error) {
      setResponsePayload(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const copyTokenToClipboard = () => {
    if (responsePayload) {
      try {
        const responseObject = JSON.parse(responsePayload);
        const token = responseObject.token || "Token not found"; // Extract token
        navigator.clipboard.writeText(token);
        setToastMessage("Token copied to clipboard!");
        setTimeout(() => setToastMessage(""), 3000); // Hide toast after 3 seconds
      } catch (error) {
        setToastMessage("Failed to copy token!");
        setTimeout(() => setToastMessage(""), 3000);
      }
    }
  };

  return (
    <>
      <div className={`${styles["token-output-heading"]}`}>
        <span className={`${styles["text"]}`}></span>Wallet Output
        <span className={`${styles["arrow"]}`}>⤵️</span>
      </div>
      <div className={`${styles["wrapper"]}`}>
        <textarea
          rows="16"
          cols="64"
          className={`${styles["token-output"]}`}
          id="token-output-text"
          value={tokenOutput}
          readOnly
        />
        {tokenOutput !== "" && (
          <div className={`${styles["button-container"]}`}>
            {applePaySettings?.merchantId === "merchant.test.decryption" ? (
              <button
                className={`${styles["tokenize-button"]}`}
                onClick={() =>
                  window.open(
                    "https://github.com/stephen-gilbert-cko/apple-pay-decrypt",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <span className={styles["text"]}>Decrypt</span>
                <span className={styles["sparkle"]}>✨</span>
              </button>
            ) : (
              <button
                className={`${styles["tokenize-button"]}`}
                onClick={toggleModal}
              >
                <span className={styles["text"]}>Tokenize</span>
                <span className={styles["sparkle"]}>✨</span>
              </button>
            )}
            <button
              id="pretty-print-button"
              ref={buttonRef}
              onClick={prettyPrint}
              style={{
                "--animation-play-state": isAnimating ? "running" : "paused",
              }}
              className={`${styles["rainbow-button"]}`}
            ></button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className={styles["modal-overlay"]}>
          <div className={styles["modal-content"]}>
            <div className={styles["title-bar"]}>
              <h3>
                {WALLET_LABELS[walletType]}
                &nbsp;&nbsp;➡️&nbsp;&nbsp;
                <span className={styles["blue-text"]}>Checkout.com</span>
              </h3>
              <button onClick={toggleModal} className={styles["close-button"]}>
                &times;
              </button>
            </div>
            <div className={styles["api-view"]}>
              <div className={styles["request"]}>
                <h4>Request</h4>
                <pre className={styles["request-input"]}>{requestPayload}</pre>
                <button
                  onClick={sendTokensRequest}
                  disabled={isLoading}
                  className={styles["send-button"]}
                >
                  {isLoading ? "Sending..." : "Send Request"}
                </button>
              </div>
              <div className={styles["response"]}>
                <h4>Response</h4>
                <pre className={styles["response-output"]}>
                  {responsePayload || "No response yet"}
                </pre>
                {responsePayload && responsePayload !== "No response yet" && (
                  <button
                    onClick={copyTokenToClipboard}
                    className={styles["copy-button"]}
                  >
                    Copy Token 📋
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div className={styles["toast"]}>
          <p>{toastMessage}</p>
        </div>
      )}
    </>
  );
};

export default WalletTokenOutput;
